import React, { lazy, Suspense, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { Provider, useSelector } from "react-redux";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import store from "./redux/store";
import { HelmetProvider, Helmet } from "react-helmet-async";
// import SupportChatWidget from './components/SupportChatWidget';

import "./App.css";
import MainLayout from "./layout/MainLayout";
import AuthLayout from "./layout/AuthLayout";
import Loader from "./components/Loader/loader";
import "./pages/About/Aboutus.css";
import "./pages/Checkout/Checkout.css";
import "./pages/Contactus/Contactus.css";
import "./pages/Diamonddetail/Diamonddetail.css";
import "./pages/Forgotpassword/Resetpassword.css";
import "./pages/Home/Home.css";
import "./pages/login/login.css";
import "./pages/Pagenotfound/Pagenotfound.css";
import "./pages/privacypolicy/privacypolicy.css";
import "./pages/register/register.css";
import "./pages/shop/shop.css";
import "./pages/Termsofuse/Termsofuse.css";
import "./pages/Userprofile/Userprofile.css";
import "./pages/Orderhistory/Orderhistory.css";
import "./components/Footer.css";
import "./components/Header.css";
import "./components/Loader/loader.css";
import "./pages/FAQ/FAQ";

const Home = lazy(() => import("./pages/Home/Home"));
const Aboutus = lazy(() => import("./pages/About/Aboutus"));
const Contactus = lazy(() => import("./pages/Contactus/Contactus"));
const Privacypolicy = lazy(() => import("./pages/privacypolicy/privacypolicy"));
const Termsofuse = lazy(() => import("./pages/Termsofuse/Termsofuse"));
const Checkout = lazy(() => import("./pages/Checkout/Checkout"));
const Login = lazy(() => import("./pages/login/login"));
const Signup = lazy(() => import("./pages/register/register"));
const Shop = lazy(() => import("./pages/shop/shop"));
const Pagenotfound = lazy(() => import("./pages/Pagenotfound/Pagenotfound"));
const Diamonddetail = lazy(() => import("./pages/Diamonddetail/Diamonddetail"));
const Activitylog = lazy(() => import("./pages/Activitylog/Activitylog"));
const Forgotpassword = lazy(() =>
  import("./pages/Forgotpassword/Forgotpassword")
);
const Resetpassword = lazy(() =>
  import("./pages/Forgotpassword/Resetpassword")
);
const UserProfile = lazy(() => import("./pages/Userprofile/UserProfile"));
const Jewellery = lazy(() => import("./pages/Jewellery/Jewellery"));
const Jewellerydetail = lazy(() => import("./pages/Jewellery/Jewellerydetail"));
const FAQ = lazy(() => import("./pages/FAQ/FAQ"));
const Orderhistory = lazy(() => import("./pages/Orderhistory/Orderhistory"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy/RefundPolicy"));
const ShippingPolicy = lazy(() =>
  import("./pages/ShippingPolicy/ShippingPolicy")
);
const PaymentSuccess = lazy(() =>
  import("./pages/PaymentResult/PaymentSuccess")
);
const PaymentFailed = lazy(() =>
  import("./pages/PaymentResult/PaymentFailed")
);
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));

// Meta component for dynamic meta tags
const Meta = ({ title, description, keywords }) => (
  <Helmet>
    <title>{title}</title>
    <meta name="description" content={description} />
    <meta name="keywords" content={keywords} />
  </Helmet>
);

function App() {
  console.error = () => {};
  console.warn = () => {};

  const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);
    return null;
  };
  const userId = useSelector((state) => state.auth?.user?.UserId);
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/jewellery")) {
      sessionStorage.setItem(
        "lastVisitedPage",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
        })
      );
    }
  }, [location]);

  return (
    <Provider store={store}>
      <HelmetProvider>
        {/* <SupportChatWidget /> */}
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Home | JPS Jewels"
                      description="Discover premium lab-grown diamonds, engagement rings, wedding bands, and fine jewellery at JPS Jewels. Shop now for exquisite craftsmanship."
                      keywords="home, lab-grown diamonds, diamond jewellery, engagement rings, wedding bands, fine jewellery, JPS Jewels"
                    />
                    <Home />
                  </>
                </MainLayout>
              }
              index
            />
            <Route
              path="/aboutus"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="About Us | JPS Jewels"
                      description="Learn about JPS Jewels, our mission, values, and commitment to quality lab-grown diamonds and fine jewellery."
                      keywords="about us, JPS Jewels, company, mission, values, lab-grown diamonds, fine jewellery"
                    />
                    <Aboutus />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/contactus"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Contact Us | JPS Jewels"
                      description="Contact JPS Jewels for inquiries about lab-grown diamonds, custom jewellery, and more. We're here to help!"
                      keywords="contact, JPS Jewels, customer service, lab-grown diamonds, custom jewellery, support"
                    />
                    <Contactus />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/privacypolicy"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Privacy Policy | JPS Jewels"
                      description="Read the privacy policy of JPS Jewels to learn how we protect your data and privacy."
                      keywords="privacy policy, JPS Jewels, data protection, privacy, user data"
                    />
                    <Privacypolicy />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/termsofuse"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Terms & Conditions | JPS Jewels"
                      description="Review the terms and conditions for JPS Jewels' website, orders, payments, and digital design licenses."
                      keywords="terms of use, JPS Jewels, website terms, user agreement"
                    />
                    <Termsofuse />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/refundpolicy"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Refund & Cancellation Policy | JPS Jewels"
                      description="Refund and cancellation policy of JPS Jewels. All products are non-refundable after purchase; refunds apply only for duplicate payment, technical failure, or non-delivery."
                      keywords="refund policy, cancellation policy, JPS Jewels, non-refundable, returns"
                    />
                    <RefundPolicy />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/shippingpolicy"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Shipping & Delivery Policy | JPS Jewels"
                      description="Shipping and delivery policy of JPS Jewels — insured shipping for jewellery and instant delivery for digital design files."
                      keywords="shipping policy, delivery policy, JPS Jewels, insured shipping, digital delivery"
                    />
                    <ShippingPolicy />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/payment-success"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Payment Successful | JPS Jewels"
                      description="Your payment to JPS Jewels was successful."
                      keywords="payment success, JPS Jewels"
                    />
                    <PaymentSuccess />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/payment-failed"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Payment Failed | JPS Jewels"
                      description="Your payment to JPS Jewels could not be completed."
                      keywords="payment failed, JPS Jewels"
                    />
                    <PaymentFailed />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/checkout"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Checkout | JPS Jewels"
                      description="Complete your purchase of lab-grown diamonds and fine jewellery at JPS Jewels. Secure and easy checkout process."
                      keywords="checkout, purchase, lab-grown diamonds, fine jewellery, JPS Jewels, payment"
                    />
                    <Checkout />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/diamond"
              element={
                  <MainLayout>
                    <>
                      <Meta
                        title="Shop Diamonds | JPS Jewels"
                        description="Shop our collection of premium lab-grown diamonds at JPS Jewels. Find the perfect diamond for your jewellery."
                        keywords="shop, diamonds, lab-grown diamonds, JPS Jewels, diamond collection"
                      />
                      <Shop />
                    </>
                  </MainLayout>
                }
              />
            <Route
              path="/diamonddetail/:SKU"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Diamond Detail | JPS Jewels"
                      description="View detailed information about this lab-grown diamond from JPS Jewels."
                      keywords="diamond detail, lab-grown diamond, JPS Jewels, diamond info"
                    />
                    <Diamonddetail />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/jewellery"
              element={
                <MainLayout>
                  <Jewellery />
                </MainLayout>
              }
            />
            {/* <Route
              path="/jewellery/:ProductId"
              element={
                <MainLayout>
                  <Jewellerydetail />
                </MainLayout>
              }
            /> */}
            <Route
              path="/jewellery/:productName"
              element={
                <MainLayout>
                  <Jewellerydetail />
                </MainLayout>
              }
            />
            <Route
              path="/activitylog"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Activity Log | JPS Jewels"
                      description="View your recent activity and order history at JPS Jewels."
                      keywords="activity log, order history, JPS Jewels, user activity"
                    />
                    <Activitylog />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/profile"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="User Profile | JPS Jewels"
                      description="Manage your user profile, account settings, and preferences at JPS Jewels."
                      keywords="user profile, account, settings, JPS Jewels"
                    />
                    <UserProfile />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/faq"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="FAQ | JPS Jewels"
                      description="Frequently asked questions about JPS Jewels, lab-grown diamonds, and our services."
                      keywords="FAQ, questions, JPS Jewels, lab-grown diamonds, help"
                    />
                    <FAQ />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/orderhistory"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Order History | JPS Jewels"
                      description="View your order history and track your purchases at JPS Jewels."
                      keywords="order history, purchases, JPS Jewels, track order"
                    />
                    <Orderhistory />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="/dashboard"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Dashboard | JPS Jewels"
                      description="Access your dashboard for insights, analytics, and account management at JPS Jewels."
                      keywords="dashboard, analytics, account, JPS Jewels"
                    />
                    <Dashboard />
                  </>
                </MainLayout>
              }
            />
            <Route
              path="*"
              element={
                <MainLayout>
                  <>
                    <Meta
                      title="Page Not Found | JPS Jewels"
                      description="Sorry, the page you are looking for does not exist."
                      keywords="404, page not found, JPS Jewels"
                    />
                    <Pagenotfound />
                  </>
                </MainLayout>
              }
            />

            {/* Routes without Header & Footer */}
            <Route
              path="/login"
              element={
                <AuthLayout>
                  <>
                    <Meta
                      title="Login | JPS Jewels"
                      description="Login to your JPS Jewels account to access exclusive features and offers."
                      keywords="login, sign in, JPS Jewels, account"
                    />
                    <Login />
                  </>
                </AuthLayout>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthLayout>
                  <>
                    <Meta
                      title="Sign Up | JPS Jewels"
                      description="Create a new account at JPS Jewels and start your journey with premium lab-grown diamonds and fine jewellery."
                      keywords="sign up, register, JPS Jewels, create account"
                    />
                    <Signup />
                  </>
                </AuthLayout>
              }
            />
            <Route
              path="/forgotpassword"
              element={
                <AuthLayout>
                  <>
                    <Meta
                      title="Forgot Password | JPS Jewels"
                      description="Reset your password for your JPS Jewels account."
                      keywords="forgot password, reset password, JPS Jewels, account recovery"
                    />
                    <Forgotpassword />
                  </>
                </AuthLayout>
              }
            />
            <Route
              path="/resetpassword"
              element={
                <AuthLayout>
                  <>
                    <Meta
                      title="Reset Password | JPS Jewels"
                      description="Set a new password for your JPS Jewels account."
                      keywords="reset password, new password, JPS Jewels, account security"
                    />
                    <Resetpassword />
                  </>
                </AuthLayout>
              }
            />
          </Routes>
        </Suspense>

        <ToastContainer />
      </HelmetProvider>
    </Provider>
  );
}

export default App;
